import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const LiveKPICards = () => {
  const [kpiData, setKpiData] = useState({
    todaysSales: { value: 0, change: 0, trend: 'up' },
    pendingCollections: { value: 0, change: 0, trend: 'down' },
    dataEntryStatus: { value: 0, total: 8, status: 'good' },
    activeUsers: { value: 0, change: 0, trend: 'up' },
    systemHealth: { value: 0, status: 'healthy' },
    processingQueue: { value: 0, change: 0, trend: 'stable' }
  });

  useEffect(() => {
    // Initial data load
    const initialData = {
      todaysSales: { value: 847250, change: 12.5, trend: 'up' },
      pendingCollections: { value: 156800, change: -8.2, trend: 'down' },
      dataEntryStatus: { value: 6, total: 8, status: 'warning' },
      activeUsers: { value: 24, change: 4.1, trend: 'up' },
      systemHealth: { value: 98.7, status: 'healthy' },
      processingQueue: { value: 12, change: 0, trend: 'stable' }
    };
    
    setKpiData(initialData);

    // Simulate real-time updates
    const interval = setInterval(() => {
      setKpiData(prev => ({
        todaysSales: {
          ...prev.todaysSales,
          value: prev.todaysSales.value + Math.floor(Math.random() * 5000) - 1000,
          change: prev.todaysSales.change + (Math.random() - 0.5) * 2
        },
        pendingCollections: {
          ...prev.pendingCollections,
          value: Math.max(0, prev.pendingCollections.value + Math.floor(Math.random() * 3000) - 2000),
          change: prev.pendingCollections.change + (Math.random() - 0.5) * 3
        },
        dataEntryStatus: {
          ...prev.dataEntryStatus,
          value: Math.min(8, Math.max(0, prev.dataEntryStatus.value + Math.floor(Math.random() * 3) - 1))
        },
        activeUsers: {
          ...prev.activeUsers,
          value: Math.max(0, prev.activeUsers.value + Math.floor(Math.random() * 3) - 1),
          change: prev.activeUsers.change + (Math.random() - 0.5) * 1
        },
        systemHealth: {
          ...prev.systemHealth,
          value: Math.min(100, Math.max(90, prev.systemHealth.value + (Math.random() - 0.5) * 2))
        },
        processingQueue: {
          ...prev.processingQueue,
          value: Math.max(0, prev.processingQueue.value + Math.floor(Math.random() * 5) - 2),
          change: prev.processingQueue.change + (Math.random() - 0.5) * 1
        }
      }));
    }, 15000); // Update every 15 seconds

    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return { icon: 'TrendingUp', color: 'text-success' };
      case 'down':
        return { icon: 'TrendingDown', color: 'text-error' };
      default:
        return { icon: 'Minus', color: 'text-muted-foreground' };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': case'good':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'critical':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const kpiCards = [
    {
      title: "Today\'s Sales",
      value: formatCurrency(kpiData.todaysSales.value),
      change: `${kpiData.todaysSales.change > 0 ? '+' : ''}${kpiData.todaysSales.change.toFixed(1)}%`,
      trend: kpiData.todaysSales.trend,
      icon: 'DollarSign',
      iconBg: 'bg-success/10',
      iconColor: 'text-success'
    },
    {
      title: "Pending Collections",
      value: formatCurrency(kpiData.pendingCollections.value),
      change: `${kpiData.pendingCollections.change > 0 ? '+' : ''}${kpiData.pendingCollections.change.toFixed(1)}%`,
      trend: kpiData.pendingCollections.trend,
      icon: 'Clock',
      iconBg: 'bg-warning/10',
      iconColor: 'text-warning'
    },
    {
      title: "Data Entry Status",
      value: `${kpiData.dataEntryStatus.value}/${kpiData.dataEntryStatus.total}`,
      change: "Companies reporting",
      trend: 'stable',
      icon: 'FileText',
      iconBg: 'bg-primary/10',
      iconColor: 'text-primary'
    },
    {
      title: "Active Users",
      value: kpiData.activeUsers.value.toString(),
      change: `${kpiData.activeUsers.change > 0 ? '+' : ''}${kpiData.activeUsers.change.toFixed(1)}%`,
      trend: kpiData.activeUsers.trend,
      icon: 'Users',
      iconBg: 'bg-secondary/10',
      iconColor: 'text-secondary'
    },
    {
      title: "System Health",
      value: formatPercentage(kpiData.systemHealth.value),
      change: "Uptime",
      trend: 'stable',
      icon: 'Activity',
      iconBg: 'bg-success/10',
      iconColor: 'text-success'
    },
    {
      title: "Processing Queue",
      value: kpiData.processingQueue.value.toString(),
      change: `${kpiData.processingQueue.change > 0 ? '+' : ''}${kpiData.processingQueue.change.toFixed(1)}%`,
      trend: kpiData.processingQueue.trend,
      icon: 'Zap',
      iconBg: 'bg-accent/10',
      iconColor: 'text-accent-foreground'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 p-4 lg:p-6">
      {kpiCards.map((card, index) => {
        const trendConfig = getTrendIcon(card.trend);
        
        return (
          <div
            key={index}
            className="bg-card border border-border rounded-lg p-4 hover:shadow-sm transition-shadow duration-200"
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${card.iconBg}`}>
                <Icon name={card.icon} size={20} className={card.iconColor} />
              </div>
              <div className="flex items-center space-x-1">
                <Icon name={trendConfig.icon} size={14} className={trendConfig.color} />
              </div>
            </div>
            
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-muted-foreground">
                {card.title}
              </h3>
              <div className="text-2xl font-bold text-foreground">
                {card.value}
              </div>
              <div className={`text-xs ${trendConfig.color}`}>
                {card.change}
              </div>
            </div>
            
            {/* Live indicator */}
            <div className="flex items-center justify-end mt-2">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span className="text-xs text-muted-foreground">Live</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LiveKPICards;